/**
 * Beta gate page — password wall shown when `BETA_PASSWORD` is set.
 * Once the visitor enters the correct password, a signed cookie is set
 * and they are redirected to the original destination (landing page,
 * signup, etc.).
 *
 * Lives outside `_auth/` and `_app/` so it renders standalone — no
 * double header or layout chrome.
 */

import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, redirect, Form, useSearchParams } from 'react-router'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { AnimatedLogo } from '#app/components/animated-logo.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
	grantBetaAccess,
	hasBetaAccess,
	isBetaGateActive,
} from '#app/utils/beta-gate.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { type Route } from './+types/beta.ts'

const BetaSchema = z.object({
	password: z.string().min(1, 'Voer het wachtwoord in'),
	redirectTo: z.string().optional(),
})

export async function loader({ request }: Route.LoaderArgs) {
	if (!isBetaGateActive || (await hasBetaAccess(request))) {
		const url = new URL(request.url)
		throw redirect(safeRedirect(url.searchParams.get('redirectTo')))
	}
	return null
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: BetaSchema })

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: 400 },
		)
	}

	const { password, redirectTo } = submission.value
	const setCookie = await grantBetaAccess(password)

	if (!setCookie) {
		return data(
			{
				result: submission.reply({
					fieldErrors: { password: ['Onjuist wachtwoord'] },
				}),
			},
			{ status: 403 },
		)
	}

	return redirect(safeRedirect(redirectTo), {
		headers: { 'Set-Cookie': setCookie },
	})
}

export default function BetaGatePage() {
	const [searchParams] = useSearchParams()
	const isPending = useIsPending()
	const redirectTo = searchParams.get('redirectTo') ?? '/'

	const [form, fields] = useForm({
		id: 'beta-gate-form',
		constraint: getZodConstraint(BetaSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: BetaSchema })
		},
	})

	return (
		<div className="flex min-h-screen items-center justify-center bg-[#18181b] px-4">
			<div className="w-full max-w-sm">
				<div className="flex flex-col items-center text-center">
					<AnimatedLogo size={120} className="mb-4" />
					<h1 className="font-display text-2xl text-neon-pink">
						MUZIEKDOCENT
					</h1>
					<p className="mt-2 text-sm text-text-muted">
						Deze app is momenteel in bèta. Voer het wachtwoord in
						om toegang te krijgen.
					</p>
				</div>

				<Form
					method="POST"
					{...getFormProps(form)}
					className="mt-8 space-y-4"
				>
					<input type="hidden" name="redirectTo" value={redirectTo} />
					<Field
						labelProps={{ children: 'Wachtwoord' }}
						inputProps={{
							...getInputProps(fields.password, { type: 'password' }),
							autoFocus: true,
							autoComplete: 'off',
							placeholder: 'Bèta wachtwoord',
						}}
						errors={fields.password.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="w-full"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
					>
						Toegang
					</StatusButton>
				</Form>
			</div>
		</div>
	)
}
